"use strict";
/**
 * Win-probability page rendering.
 *
 * Baseline P(home), Kalshi's top of book, and NFL QB event controls.
 * Phone-first: rows and controls are tap targets.
 *
 * Every formatter here is pure and returns a placeholder rather than throwing:
 * a live feed goes missing mid-game and the page must degrade to a dash, not
 * a blank screen.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY = void 0;
exports.formatProbability = formatProbability;
exports.formatCents = formatCents;
exports.formatQuote = formatQuote;
exports.quoteMid = quoteMid;
exports.formatEdge = formatEdge;
exports.formatClock = formatClock;
exports.formatSituation = formatSituation;
exports.renderGameList = renderGameList;
exports.renderGameDetail = renderGameDetail;
const win_prob_1 = require("../types/win-prob");
exports.EMPTY = "—";
/** 0.684 → "68.4%". */
function formatProbability(p) {
    return typeof p === "number" && Number.isFinite(p) ? `${(p * 100).toFixed(1)}%` : exports.EMPTY;
}
/** Dollars 0..1 → cents, the unit the operator reads prices in. */
function formatCents(v) {
    return typeof v === "number" && Number.isFinite(v) ? `${Math.round(v * 100)}` : exports.EMPTY;
}
function formatQuote(quote) {
    if (!quote)
        return exports.EMPTY;
    return `${formatCents(quote.bid)} / ${formatCents(quote.ask)}`;
}
/** Mid of the top of book, or null when either side is missing. */
function quoteMid(quote) {
    if (!quote || typeof quote.bid !== "number" || typeof quote.ask !== "number")
        return null;
    return (quote.bid + quote.ask) / 2;
}
/**
 * Model minus market, in cents. Positive means the model prices home above the
 * market. Signed on purpose: the sign is the whole message.
 */
function formatEdge(homeWp, quote) {
    const mid = quoteMid(quote);
    if (mid === null || typeof homeWp !== "number" || !Number.isFinite(homeWp))
        return exports.EMPTY;
    const cents = (homeWp - mid) * 100;
    return `${cents >= 0 ? "+" : ""}${cents.toFixed(1)}`;
}
/** "Q2 07:31", or the status word when the clock means nothing. */
function formatClock(state) {
    if (!state)
        return exports.EMPTY;
    if (state.status === "pre")
        return "pre-game";
    if (state.status === "half")
        return "halftime";
    if (state.status === "final")
        return "final";
    const seconds = Math.max(0, Math.round(state.clockSeconds));
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `Q${state.period} ${mm}:${String(ss).padStart(2, "0")}`;
}
/** "2nd & 7 at 35", or a dash between drives. */
function formatSituation(state) {
    if (!state || state.down === null || state.distance === null)
        return exports.EMPTY;
    const ordinal = ["1st", "2nd", "3rd", "4th"][state.down - 1] ?? `${state.down}th`;
    const spot = state.yardsToEndzone === null ? "" : ` at ${state.yardsToEndzone}`;
    return `${ordinal} & ${state.distance}${spot}`;
}
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
function kickoffLabel(iso) {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime()))
        return exports.EMPTY;
    return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
/**
 * One row per game: a wide tap target to open it, plus a track button when the
 * scheduler has not picked it up. The row is a div, not a button, because the
 * track control nests inside it and a button inside a button is invalid.
 */
function renderGameList(games) {
    if (games.length === 0) {
        return '<p class="wp-empty">No games today. The tracker picks a game up 60 minutes before kickoff.</p>';
    }
    return games
        .map((game) => {
        const label = `${escapeHtml(game.away.abbreviation)} @ ${escapeHtml(game.home.abbreviation)}`;
        const attrs = `data-league="${escapeHtml(game.league)}" data-game-id="${escapeHtml(game.gameId)}"`;
        const track = game.tracked
            ? '<span class="wp-badge wp-badge-live">tracked</span>'
            : `<button type="button" class="wp-track btn btn-small" ${attrs}>track</button>`;
        // The pad needs no tracker for the game, so it is offered on every row.
        const action = `${track}<button type="button" class="wp-open-tap btn btn-small" ${attrs}>pad</button>`;
        return `<div class="wp-game-row" ${attrs}>
				<button type="button" class="wp-open" ${attrs}>
					<span class="wp-game-teams">${label}</span>
					<span class="wp-game-meta">${escapeHtml(kickoffLabel(game.kickoffAt))} · ${escapeHtml(game.status)}</span>
				</button>
				${action}
			</div>`;
    })
        .join("");
}
/** The single-game view: model, market, and the gap. */
function renderGameDetail(view) {
    const listing = view.listing;
    const snapshot = view.latest;
    // Hockey never reaches this page (its page is the pad); the football fields are what this view formats.
    const state = (0, win_prob_1.isWpHockeyState)(snapshot?.state) ? null : snapshot?.state ?? null;
    const home = view.quotes.find((q) => q.outcome === "home") ?? null;
    const homeWp = snapshot?.result.homeWp ?? null;
    const personnel = view.personnel;
    const quoteReady = view.quoteReady === true;
    const showDiagnosticGap = quoteReady || view.personnelCommandsEnabled === false;
    const title = listing
        ? `${escapeHtml(listing.away.abbreviation)} @ ${escapeHtml(listing.home.abbreviation)}`
        : escapeHtml(view.gameId);
    const homeName = listing ? escapeHtml(listing.home.abbreviation) : "home";
    const warnings = [];
    if (view.feedStale)
        warnings.push("feed stale");
    if (view.missedPolls > 0)
        warnings.push(`${view.missedPolls} missed polls`);
    if (snapshot?.result.reason)
        warnings.push(escapeHtml(snapshot.result.reason));
    if (!home)
        warnings.push("no Kalshi market linked");
    if (personnel && personnel.status !== "normal")
        warnings.push(personnel.reason ?? personnel.status);
    const qbControls = listing?.league === "nfl" && state?.status !== "final" && view.personnelCommandsEnabled
        ? `<div class="wp-qb-controls" aria-label="Quarterback status">
			${["away", "home"].map((side) => `<div class="wp-qb-side">
				<span>${escapeHtml(side === "home" ? listing.home.abbreviation : listing.away.abbreviation)} QB</span>
				<button type="button" class="wp-personnel wp-qb-out" data-kind="qb_exit_suspected" data-side="${side}">QB OUT</button>
				<button type="button" class="wp-personnel" data-kind="backup_confirmed" data-side="${side}">backup in</button>
				<button type="button" class="wp-personnel" data-kind="starter_returned" data-side="${side}">returned</button>
				<button type="button" class="wp-personnel" data-kind="operator_cleared" data-side="${side}">clear</button>
				<input class="wp-qb-name" data-side="${side}" aria-label="${side} QB name, optional" placeholder="QB name (optional)" maxlength="120" />
			</div>`).join("")}
			<button type="button" class="wp-personnel wp-correct-last" data-kind="correction" ${view.personnelEvents?.length ? "" : "disabled"}>Correct last QB event</button>
		</div>` : "";
    return `<div class="wp-detail">
		<div class="wp-detail-head">
			<h3>${title}</h3>
			<span class="wp-detail-clock">${escapeHtml(formatClock(state))}</span>
			<button type="button" class="wp-open-tap btn btn-small" data-league="${escapeHtml(listing?.league ?? "nfl")}" data-game-id="${escapeHtml(view.gameId)}">tap pad</button>
		</div>
		<div class="wp-score">${escapeHtml(String(state?.awayScore ?? exports.EMPTY))} – ${escapeHtml(String(state?.homeScore ?? exports.EMPTY))}</div>
		<div class="wp-situation">${escapeHtml(formatSituation(state))}</div>
		<div class="wp-readiness ${quoteReady ? "wp-ready" : "wp-do-not-quote"}" role="status">${quoteReady ? "Baseline model available" : `DO NOT QUOTE · ${escapeHtml(view.personnelCommandsEnabled === false ? "QB control is not connected to this tracker" : personnel?.reason ?? snapshot?.result.reason ?? "status unavailable")}`}</div>
		<div class="wp-numbers">
			<div class="wp-number">
				<span class="wp-number-label">baseline model ${homeName}</span>
				<span class="wp-number-value wp-number-big">${escapeHtml(formatProbability(homeWp))}</span>
			</div>
			<div class="wp-number">
				<span class="wp-number-label">Kalshi bid / ask</span>
				<span class="wp-number-value">${escapeHtml(formatQuote(home))}</span>
			</div>
			<div class="wp-number">
				<span class="wp-number-label">model − market ¢${quoteReady ? "" : " (shadow)"}</span>
				<span class="wp-number-value wp-number-big wp-edge-value">${showDiagnosticGap ? escapeHtml(formatEdge(homeWp, home)) : exports.EMPTY}</span>
			</div>
		</div>
		<div class="wp-meta">
			<span>ESPN ${escapeHtml(formatProbability(snapshot?.feedHomeWp ?? null))}</span>
			<span>spread ${escapeHtml(snapshot?.pregame.spreadLine === null || snapshot?.pregame.spreadLine === undefined ? exports.EMPTY : String(snapshot.pregame.spreadLine))}</span>
			<span>${escapeHtml(snapshot?.result.modelVersion ?? exports.EMPTY)}</span>
		</div>
		${warnings.length ? `<div class="wp-warning">${escapeHtml(warnings.join(" · "))}</div>` : ""}
		${qbControls}
	</div>`;
}
